/**
 * LoadProfileSection - Load Modeling Tab Content
 *
 * Extracts the load profile configuration from ScenarioEditor Tab 2.
 * Contains:
 * - Base Facility Load status (read-only, managed at meter level)
 * - EV Charging configuration
 * - Existing Solar configuration
 * - Combined Load Preview Chart
 */

import React, { useState } from 'react';
import {
    Box,
    Button,
    Card,
    Typography,
    Alert,
    IconButton,
} from '@mui/material';
import {
    ElectricBolt,
    DirectionsCar,
    ElectricCar,
    Delete as DeleteIcon,
    ShowChart,
} from '@mui/icons-material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { SectionCard, PowerGraph, vortxColors } from '@vortx/shared-ui';
import { ExistingSolarConfiguration } from '../../../components/scenario/ExistingSolarConfiguration';
import ImportFluxEVDialog from '../../../components/ImportFluxEVDialog';

/**
 * EV Charging Card
 * Import EV charging load from Flux EV.
 */
function EVChargingCard({
    onImportFluxEV,
    onRemoveEVLoad,
    evLoadSource,
}) {
    return (
        <SectionCard title="EV Charging" icon={<DirectionsCar color="primary" />}>
            <Box sx={{ mb: 3 }}>
                <Button
                    variant="outlined"
                    size="medium"
                    startIcon={<ElectricCar />}
                    onClick={onImportFluxEV}
                    fullWidth
                >
                    Import from Flux EV
                </Button>
            </Box>

            {evLoadSource?.source === 'flux_ev' && (
                <Alert
                    severity="success"
                    sx={{ mb: 2 }}
                    action={
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button color="inherit" size="small" onClick={onImportFluxEV}>
                                Replace
                            </Button>
                            <IconButton
                                size="small"
                                color="inherit"
                                onClick={onRemoveEVLoad}
                                title="Remove EV Load"
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    }
                >
                    Imported from <strong>{evLoadSource.projectName}</strong>
                    {evLoadSource.modelType && ` (${evLoadSource.modelType})`}
                    {evLoadSource.peakKw != null && ` \u2014 Peak: ${Math.round(evLoadSource.peakKw)} kW`}
                    {evLoadSource.annualKwh != null && `, Annual: ${Math.round(evLoadSource.annualKwh).toLocaleString()} kWh`}
                </Alert>
            )}

        </SectionCard>
    );
}

/**
 * Load Preview Card
 * Shows combined load chart with day navigation.
 */
function LoadPreviewCard({
    loads,
    startDate,
    dayIndex,
    setDayIndex,
    peakLoadDay,
    graphData,
    loadSummary,
    existingSolarProfile,
    isDark,
}) {
    const hasLoad = loads?.length === 8760;

    return (
        <Card sx={{ mt: 3, p: 2 }}>
            <Typography variant="h6">Total Load Preview</Typography>

            {hasLoad ? (
                <>
                    {/* Day Navigation */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2, mb: 1 }}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker
                                label="Select Day"
                                value={new Date(new Date(startDate).getTime() + dayIndex * 24 * 3600 * 1000)}
                                onChange={(newDate) => {
                                    if (newDate) {
                                        const diff = Math.round((newDate - new Date(startDate)) / (24 * 3600 * 1000));
                                        setDayIndex(Math.min(364, Math.max(0, diff)));
                                    }
                                }}
                                slotProps={{
                                    textField: {
                                        size: "small",
                                        sx: { maxWidth: 150 }
                                    }
                                }}
                            />
                        </LocalizationProvider>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            Day {dayIndex + 1} of 365
                            {dayIndex === peakLoadDay && ' • Peak load day'}
                        </Typography>
                    </Box>

                    <Box sx={{ height: 450, mt: 2 }}>
                        <PowerGraph
                            data={graphData}
                            availableKeys={existingSolarProfile?.length === 8760
                                ? ['load', 'solar_to_load', 'utility']
                                : ['load', 'utility']}
                            isDark={isDark}
                            showControls={false}
                        />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 4, mt: 2, p: 2, bgcolor: isDark ? 'rgba(255,255,255,0.05)' : '#f5f5f5', borderRadius: 1 }}>
                        <Box>
                            <Typography variant="caption">Net Annual Energy</Typography>
                            <Typography variant="h6">{loadSummary.total.toLocaleString(undefined, { maximumFractionDigits: 0 })} kWh</Typography>
                        </Box>
                        <Box>
                            <Typography variant="caption">Peak Demand</Typography>
                            <Typography variant="h6">{loadSummary.peak.toLocaleString(undefined, { maximumFractionDigits: 0 })} kW</Typography>
                        </Box>
                        {existingSolarProfile?.length === 8760 && (
                            <Box>
                                <Typography variant="caption">Existing Solar Production</Typography>
                                <Typography variant="h6">{existingSolarProfile.reduce((sum, val) => sum + val, 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} kWh</Typography>
                            </Box>
                        )}
                    </Box>
                </>
            ) : (
                <Box sx={{ py: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', opacity: 0.6 }}>
                    <ShowChart sx={{ fontSize: 60, mb: 2, color: vortxColors.gray }} />
                    <Typography variant="body1" color="textSecondary">
                        Complete your load profile setup to view the preview.
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                        Upload a Base Facility Load or enable EV Simulation.
                    </Typography>
                </Box>
            )}
        </Card>
    );
}

/**
 * LoadProfileSection Component
 *
 * Main component for Tab 2 (Load Modeling) content.
 * Base facility load is now managed at the meter level (project Meters & Tariffs page).
 *
 * @param {Object} props
 * @param {Object} props.loadProfile - Load profile hook state
 * @param {Object} props.tariffLocation - Location from tariff (lat/lon)
 * @param {Function} props.onSnackbar - Handler for snackbar notifications
 * @param {number} props.loadPreviewDayIndex - Current day index for preview
 * @param {Function} props.setLoadPreviewDayIndex - Setter for day index
 * @param {number} props.peakLoadDay - Day index with peak load
 * @param {Array} props.loadPreviewGraphData - Graph data for preview
 * @param {boolean} props.isDark - Dark mode flag
 */
function LoadProfileSection({
    loadProfile,
    tariffLocation,
    onSnackbar,
    loadPreviewDayIndex,
    setLoadPreviewDayIndex,
    peakLoadDay,
    loadPreviewGraphData,
    isDark,
}) {
    const [importDialogOpen, setImportDialogOpen] = useState(false);

    return (
        <>
            {/* 1. Base Facility Load (managed at meter level) */}
            {loadProfile.baseLoads?.length === 8760 ? (
                <Alert
                    severity="info"
                    sx={{ mb: 2 }}
                    icon={<ElectricBolt />}
                >
                    <Typography variant="body2">
                        Base facility load: <strong>{loadProfile.loadFileName || '8760 hourly values'}</strong>
                        {' '}&mdash; managed in project Meters &amp; Tariffs
                    </Typography>
                </Alert>
            ) : (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    No facility load profile. Upload a CSV in the project's <strong>Meters &amp; Tariffs</strong> page.
                </Alert>
            )}

            {/* 2. EV / Additive Load */}
            <EVChargingCard
                isEvEnabled={loadProfile.isEvEnabled}
                setIsEvEnabled={loadProfile.setIsEvEnabled}
                loadModel={loadProfile.loadModel}
                setLoadModel={loadProfile.setLoadModel}
                generateLoadModelProfile={loadProfile.generateLoadModelProfile}
                onSuccess={(msg) => onSnackbar({ open: true, message: msg, severity: "success" })}
                onError={(msg) => onSnackbar({ open: true, message: msg, severity: "error" })}
                onImportFluxEV={() => setImportDialogOpen(true)}
                onRemoveEVLoad={loadProfile.removeEVLoad}
                evLoadSource={loadProfile.evLoadSource}
            />

            {/* Import from Flux EV Dialog */}
            <ImportFluxEVDialog
                open={importDialogOpen}
                onClose={() => setImportDialogOpen(false)}
                onImport={(loads, sourceInfo) => {
                    loadProfile.importFluxEVLoad(loads, sourceInfo);
                    onSnackbar({
                        open: true,
                        message: `EV load imported from ${sourceInfo.projectName} / ${sourceInfo.scenarioName}`,
                        severity: 'success',
                    });
                }}
            />

            {/* 3. Existing Solar */}
            <Box sx={{ mt: 3 }}>
                <ExistingSolarConfiguration
                    latitude={tariffLocation?.lat}
                    longitude={tariffLocation?.lon}
                    existingSolarParams={loadProfile.existingSolarParams}
                    existingSolarProfile={loadProfile.existingSolarProfile}
                    baseLoadIsNetOfSolar={loadProfile.baseLoadIsNetOfSolar}
                    existingSolarCanExport={loadProfile.existingSolarCanExport}
                    onSimulate={(params, profile, rawOutputs) => {
                        loadProfile.setExistingSolarParams(params);
                        loadProfile.setExistingSolarProfile(profile);
                        onSnackbar({
                            open: true,
                            message: `Existing solar configured! Annual: ${rawOutputs.ac_annual.toLocaleString()} kWh`,
                            severity: 'success'
                        });
                    }}
                    onClear={() => {
                        loadProfile.setExistingSolarParams({});
                        loadProfile.setExistingSolarProfile([]);
                        onSnackbar({
                            open: true,
                            message: 'Existing solar cleared',
                            severity: 'info'
                        });
                    }}
                    onBaseLoadModeChange={(isNet) => loadProfile.setBaseLoadIsNetOfSolar(isNet)}
                    onExportModeChange={(canExport) => loadProfile.setExistingSolarCanExport(canExport)}
                />
            </Box>

            {/* 4. Combined Chart */}
            <LoadPreviewCard
                loads={loadProfile.loads}
                startDate={loadProfile.startDate}
                dayIndex={loadPreviewDayIndex}
                setDayIndex={setLoadPreviewDayIndex}
                peakLoadDay={peakLoadDay}
                graphData={loadPreviewGraphData}
                loadSummary={loadProfile.loadSummary}
                existingSolarProfile={loadProfile.existingSolarProfile}
                isDark={isDark}
            />
        </>
    );
}

export default LoadProfileSection;
